import clsx from "clsx";
import {
  PROJECT_COMPLETION_STATUS_OPTIONS,
  type ProjectCompletionClientFilters,
  type ProjectCompletionRow,
  listProjectCompletionKunden,
} from "@/lib/projectCompletions";

export interface ProjectCompletionsFilterBarProps {
  filters: ProjectCompletionClientFilters;
  searchQuery: string;
  rows: ProjectCompletionRow[];
  onChange: (patch: Partial<ProjectCompletionClientFilters>) => void;
  onReset?: () => void;
}

export function ProjectCompletionsFilterBar({
  filters,
  searchQuery,
  rows,
  onChange,
  onReset,
}: ProjectCompletionsFilterBarProps) {
  const kunden = listProjectCompletionKunden(rows);
  const active =
    Boolean(searchQuery.trim()) || Boolean(filters.kunde) || filters.status !== "all";

  const tileSurface = clsx(
    "rounded-ios-lg bg-fill-quaternary/20 px-4 py-3 shadow-ios md:px-5 md:py-4",
    "ring-1 ring-separator/30 dark:ring-separator/45",
  );
  const scrollRow =
    "-mx-0.5 flex flex-nowrap gap-2 overflow-x-auto overflow-y-hidden px-0.5 pb-1.5 [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]";
  const wrapRow = "flex flex-wrap gap-2";

  return (
    <div className={clsx(tileSurface, "space-y-4")}>
      <div className="space-y-2">
        <SectionLabel>Kunde auswählen</SectionLabel>
        <div className={scrollRow}>
          <ChipButton active={!filters.kunde} onClick={() => onChange({ kunde: "" })}>
            Alle
          </ChipButton>
          {kunden.map((kunde) => (
            <ChipButton
              key={kunde}
              active={filters.kunde === kunde}
              onClick={() => onChange({ kunde: filters.kunde === kunde ? "" : kunde })}
            >
              {kunde}
            </ChipButton>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <SectionLabel>Status filtern</SectionLabel>
        <div className={wrapRow}>
          {PROJECT_COMPLETION_STATUS_OPTIONS.map((option) => (
            <ChipButton
              key={option.value}
              active={filters.status === option.value}
              onClick={() => onChange({ status: option.value })}
            >
              {option.label}
            </ChipButton>
          ))}
        </div>
      </div>

      {active && onReset ? (
        <button
          type="button"
          onClick={onReset}
          className="text-sm font-semibold text-accent hover:underline"
        >
          Filter zurücksetzen
        </button>
      ) : null}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
      {children}
    </h3>
  );
}

function ChipButton({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition",
        active
          ? "bg-accent text-label-on-accent shadow-sm"
          : "bg-fill-tertiary text-label hover:bg-fill",
      )}
    >
      {children}
    </button>
  );
}
